import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, Button, Card, CardActionArea, Chip, ClickAwayListener, IconButton, Paper, Popper, Stack, Tooltip, Typography } from '@mui/material';
import { ArrowForward, Refresh } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';

function PulseEventCard({ summary }) {
  const [anchor, setAnchor] = useState(null);
  const timer = useRef();
  const popup = useRef(null);
  const button = useRef(null);
  const id = useId();
  const cancelClose = () => clearTimeout(timer.current);
  const close = () => { cancelClose(); setAnchor(null); };
  const scheduleClose = () => {
    cancelClose();
    timer.current = setTimeout(() => {
      if (!button.current?.contains(document.activeElement) && !popup.current?.contains(document.activeElement)) setAnchor(null);
    }, 150);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const open = event => { cancelClose(); setAnchor(event.currentTarget); };
  const handleBlur = event => {
    if (!button.current?.contains(event.relatedTarget) && !popup.current?.contains(event.relatedTarget)) close();
  };
  const summaryText = Object.entries(summary.counts).map(([kind, count]) =>
    `${count} ${{ task: 'task', rsvp: 'RSVP follow-up', payment: 'payment', budget: 'budget warning', date_review: 'date review' }[kind] || kind}${count === 1 ? '' : 's'}`).join(' · ');
  return (
    <ClickAwayListener onClickAway={close}>
      <Box sx={cardItemSx(280)} onKeyDown={event => {
        if (event.key === 'Escape') { event.stopPropagation(); close(); }
        if (event.key === 'Tab' && !event.shiftKey && event.target === button.current && anchor) {
          const link = popup.current?.querySelector('a');
          if (link) { event.preventDefault(); link.focus(); }
        }
      }}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardActionArea ref={button} aria-label={`${summary.event_title}: ${summary.total} attention item${summary.total === 1 ? '' : 's'}`} aria-expanded={Boolean(anchor)} aria-controls={anchor ? id : undefined}
            onMouseEnter={open} onMouseLeave={scheduleClose} onFocus={open} onBlur={handleBlur} onClick={open} sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography fontWeight={700} noWrap sx={{ minWidth: 0 }}>{summary.event_title}</Typography>
              <Chip label={summary.total} size="small" color="warning" sx={{ flexShrink: 0 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{summaryText}</Typography>
          </CardActionArea>
        </Card>
        <Popper open={Boolean(anchor)} anchorEl={anchor} placement="bottom-start" modifiers={[{ name: 'offset', options: { offset: [0, 8] } }, { name: 'preventOverflow', options: { padding: 12 } }]} sx={{ zIndex: theme => theme.zIndex.modal + 1 }}>
          <Paper ref={popup} id={id} role="region" aria-label={`${summary.event_title} attention preview`} elevation={8}
            onMouseEnter={cancelClose} onMouseLeave={scheduleClose} onFocus={cancelClose} onBlur={handleBlur}
            sx={{ width: 320, maxWidth: 'calc(100vw - 24px)', p: 2, borderRadius: 3, border: 1, borderColor: 'divider' }}>
            <Typography fontWeight={700} sx={{ mb: 1, overflowWrap: 'anywhere' }}>{summary.event_title}</Typography>
            {summary.preview.map((item, index) => <Typography key={index} variant="body2" color={item.severity === 'error' ? 'error.main' : 'text.secondary'} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider', overflowWrap: 'anywhere' }}>{item.title}</Typography>)}
            {summary.total > summary.preview.length && <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>{summary.total - summary.preview.length} more item{summary.total - summary.preview.length === 1 ? '' : 's'} in this event</Typography>}
            <Button component={Link} to={`/events/${summary.event_id}/setup`} endIcon={<ArrowForward />} size="small" sx={{ mt: 1 }}>Open Planning Pulse</Button>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default function PulseEventCards({ summaries, onRefresh }) {
  return <Box component="section" aria-label="Planning Pulse" sx={{ mb: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="h6" fontWeight={700}>Planning Pulse</Typography>
      <Tooltip title="Refresh" arrow><IconButton aria-label="Refresh" size="small" color="primary" onClick={onRefresh}><Refresh fontSize="small" /></IconButton></Tooltip>
    </Stack>
    <Box sx={cardGridSx(240, 1.5)}>
      {summaries.map(summary => <PulseEventCard key={summary.event_id} summary={summary} />)}
    </Box>
  </Box>;
}
